import { useState } from "react"
import { DateRangePicker } from "../../shared/dateRangePicker"
import { SearchSelect } from "../../shared/searchSelect"
import { SearchFiltersModal } from "../../shared/searchFiltersModal"
import stringHelpers from "../../../utilities/stringHelpers"
import { toDateString, dateRange } from "../../../utilities/dateUtilities"
import api from "../../api/api"
import { reportDatePresetByLabel } from "../../reports/reportsEnums"
import { TagSelector } from "../../shared/tagSelector"
import appliedFiltersUtilities from "../../../utilities/appliedFiltersUtilities"
import arrayUtilities from "../../../utilities/arrayUtilities"

export const VehicleAnalyticsFiltersModal = ({
    showModal,
    handleClose,
    handleConfirm,
    initialFromDate,
    initialToDate,
    initialDatePreset,
    initialClientId,
    initialSiteId,
    initialSitesByClient,
    initialSelectedTags = [],
    initialAppliedFilters,
    clients = [],
    sites = [],
    tags,
    organisationId,
    token
}) => {
    const [fromDate, setFromDate] = useState(initialFromDate)
    const [toDate, setToDate] = useState(initialToDate)
    const [datePreset, setDatePreset] = useState(initialDatePreset)
    const [clientId, setClientId] = useState(initialClientId)
    const [siteId, setSiteId] = useState(initialSiteId)
    const [sitesByClient, setSitesByClient] = useState(initialSitesByClient)
    const [selectedTags, setSelectedTags] = useState(initialSelectedTags)
    const [appliedFilters, setAppliedFilters] = useState(initialAppliedFilters)

    const isDateRangeInvalid = fromDate === null || fromDate === "" || toDate === null || toDate === ""

    const clearLocalFilters = () => {
        const defaultRange = dateRange("currentYear")
        setFromDate(defaultRange.firstDate)
        setToDate(defaultRange.lastDate)
        setDatePreset(reportDatePresetByLabel["This year"])
        setClientId("")
        setSiteId("")
        setSitesByClient(sites)
        setSelectedTags([])
        setAppliedFilters({
            "Date Range": `${ toDateString(new Date(defaultRange.firstDate)) } - ${ toDateString(new Date(defaultRange.lastDate)) }`
        })
    }

    const handleTagsChange = tagSelections => {
        setSelectedTags(tagSelections)
        setAppliedFilters(prevState => appliedFiltersUtilities.applyMultiSelection(prevState, "Tags", tagSelections, tags))
    }

    return (
        <SearchFiltersModal
            title="Filter Results"
            handleClose={ handleClose }
            handleConfirm={ () => handleConfirm({ fromDate, toDate, datePreset, clientId, siteId, sitesByClient, selectedTags, appliedFilters }) }
            showModal={ showModal }
            key="vehicle-analytics-filters-modal"
            handleReset={ clearLocalFilters }
            disableConfirm={ isDateRangeInvalid }
        >
            <div className="w-100">
                <div className="mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Date Range</label>
                    <DateRangePicker
                        fromDate={ fromDate }
                        toDate={ toDate }
                        setDateRange={ (firstDate, lastDate, presetLabel) => {
                            const isFirstDateEmpty = firstDate === null || firstDate === ""
                            const isLastDateEmpty = lastDate === null || lastDate === ""
                            setFromDate(isFirstDateEmpty ? null : firstDate);
                            setToDate(isLastDateEmpty ? null : lastDate);
                            setDatePreset(stringHelpers.isNullOrEmpty(presetLabel) ? null : (reportDatePresetByLabel[presetLabel] ?? null))
                            if (!stringHelpers.isNullOrEmpty(presetLabel)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = presetLabel
                                    return updatedFilters
                                })
                            }
                            else if (!isFirstDateEmpty && !isLastDateEmpty) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters.From
                                    delete updatedFilters.To
                                    updatedFilters["Date Range"] = `${ toDateString(new Date(firstDate)) } - ${ toDateString(new Date(lastDate)) }`
                                    return updatedFilters
                                })
                            }
                        } }
                        hideLabel
                        fullWidth
                        dropdownPositionRight
                    />
                    { isDateRangeInvalid && <p className="text-danger fs--1 m-0 mt-1">Please enter a valid date range</p> }
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Client</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...clients.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setClientId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setSitesByClient(sites)
                                setSiteId("")
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Client"]
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                api.getClientSites(
                                    data => {
                                        setSitesByClient(data)
                                        setSiteId("")
                                    },
                                    error => console.log(error),
                                    value,
                                    organisationId,
                                    token
                                )
                                const selectedClient = clients.find(item => item.id === value)
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState, "Client": selectedClient?.name }
                                    delete updatedFilters["Site"]
                                    return updatedFilters
                                })
                            }
                        } }
                        selected={ clientId }
                    />
                </div>

                <div className="d-flex flex-column mb-2">
                    <label className="m-0 mr-2 mb-1 nowrap">Site</label>
                    <SearchSelect
                        defaultLabel="All"
                        width="100%"
                        options={ [{ value: "", label: "All" }, ...sitesByClient.map(item => ({ value: item.id, label: item.name }))] }
                        onSelect={ value => {
                            setSiteId(value)
                            if (stringHelpers.isNullOrEmpty(value)) {
                                setAppliedFilters(prevState => {
                                    const updatedFilters = { ...prevState }
                                    delete updatedFilters["Site"]
                                    return Object.keys(updatedFilters).length > 0 ? updatedFilters : null
                                })
                            } else {
                                const selectedSite = sitesByClient.find(item => item.id === value)
                                setAppliedFilters(prevState => ({ ...prevState, "Site": selectedSite?.name }))
                            }
                        } }
                        selected={ siteId }
                    />
                </div>

                {
                    !arrayUtilities.isNullOrEmpty(tags) && <div className="d-flex flex-column mb-2">
                        <div className="d-flex align-items-center justify-content-between">
                            <label className="m-0 mr-2 mb-1 nowrap">Tags</label>
                            <button onClick={ () => handleTagsChange([]) } className="btn btn-none m-0 p-0 fs--1 mb-1" type="button">Clear tags</button>
                        </div>
                        <TagSelector
                            onChange={ handleTagsChange }
                            selectedTags={ selectedTags }
                            tags={ tags }
                        />
                    </div> 
                }
            </div>
        </SearchFiltersModal>
    )
}
